import type { AiChatStreamEvent } from '@/utils/aiThought';

const MAX_SESSION_TITLE_LEN = 15;

const TASK_TITLE_MAP: Record<string, string> = {
  '【生成 BOM】': 'BOM生成',
  '【成本核算】': '成本核价',
  '【深度审查】': '文档深度审查',
  '【初步审查】': '文档初步审查',
  '【通用部件推荐】': '部件推荐',
  '【BOM 导入】': '部件库录入',
  '【自动填充】': '部件自动填充',
  '【图纸审核】': '图纸评审',
  '【TR 评审】': 'TR评审',
};

/** 截断并清理会话标题（最多 15 字） */
export function clampSessionTitle(raw: string): string {
  const t = String(raw ?? '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^["'「『【]|["'」』】]$/g, '')
    .replace(/^(标题[:：]\s*)/, '');
  if (!t) return '对话';
  return t.length > MAX_SESSION_TITLE_LEN ? t.slice(0, MAX_SESSION_TITLE_LEN) : t;
}

/** 从首条用户消息推断任务型会话标题 */
export function inferTaskSessionTitle(firstUserMessage: string): string | null {
  const text = String(firstUserMessage ?? '').trim();
  if (!text) return null;
  for (const [prefix, title] of Object.entries(TASK_TITLE_MAP)) {
    if (text.startsWith(prefix)) return title;
  }
  return null;
}

/** 从流式 intent_router 步骤提取大模型生成的 sessionTitle */
export function extractSessionTitleFromStreamEvent(event: AiChatStreamEvent): string | null {
  if (String(event.step ?? '') !== 'intent_router') return null;
  const data = event.data;
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  const raw = o.sessionTitle ?? o.session_title;
  if (raw == null) return null;
  const title = clampSessionTitle(String(raw));
  return title === '对话' ? null : title;
}

/** 从 thought JSON 提取 session_title */
export function extractSessionTitleFromThought(thoughtRaw: unknown): string | null {
  if (thoughtRaw == null) return null;
  try {
    const parsed =
      typeof thoughtRaw === 'string'
        ? (JSON.parse(thoughtRaw) as Record<string, unknown>)
        : (thoughtRaw as Record<string, unknown>);
    const raw = parsed.sessionTitle ?? parsed.session_title;
    if (raw == null) return null;
    const title = clampSessionTitle(String(raw));
    return title === '对话' ? null : title;
  } catch {
    return null;
  }
}

/**
 * 供 LLM 标题接口使用的用户侧文本：去掉引导标签与过长空白，保留语义要点。
 */
export function normalizeMessageForTitleSuggest(firstUserMessage: string): string {
  let text = String(firstUserMessage ?? '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  // 去掉【任务标签】与行首 @指令，避免标题变成「@创建项目请按三步…」
  text = text.replace(/^【[^】]{1,20}】\s*/, '');
  text = text.replace(/^@[^\s]+\s*/, '');
  return text.slice(0, 400);
}

/** 根据首条用户消息生成默认标题（无 LLM 时的兜底） */
export function fallbackSessionTitle(firstUserMessage: string): string {
  const task = inferTaskSessionTitle(firstUserMessage);
  if (task) return task;
  const normalized = normalizeMessageForTitleSuggest(firstUserMessage) || firstUserMessage;
  return clampSessionTitle(normalized);
}
